import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EntityCollectionServiceElementsFactory } from '@ngrx/data';
import {
  AdminPanelLinkInterface,
  ErrorInterface,
  NodeInterface,
} from '@shared/api';
import { environment } from '@shared/environments';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { CustomEntityCollectionBaseService } from '../entity-collection-base.class';
import { EntityTypeEnum } from '../entity.interface';

@Injectable({
  providedIn: 'root',
})
export class NodesService extends CustomEntityCollectionBaseService<NodeInterface> {
  private readonly http = inject(HttpClient);
  private readonly toastrService = inject(ToastrService);

  constructor(elementsFactory: EntityCollectionServiceElementsFactory) {
    super(EntityTypeEnum.Node, elementsFactory);
  }

  rebootNodes(nodes: NodeInterface[]): Observable<unknown> {
    return this.http
      .post(`${environment.apiHost}/nodes/run-reboot-command`, {
        items: nodes.map((node) => ({
          guid: node.guid,
        })),
      })
      .pipe(
        tap((response: any) => {
          this.refresh();
          this.handleOperationError(response['errors'] ?? []);
        }),
      );
  }

  updateNodes(nodes: NodeInterface[]): Observable<unknown> {
    return this.http
      .post(`${environment.apiHost}/nodes/run-os-update-command`, {
        items: nodes.map((node) => ({
          guid: node.guid,
          osVersionRelease: node.status.osVersionUpdate?.release,
          osVersionSandbox: node.status.osVersionUpdate?.sandbox,
          osVersionTitle: node.status.osVersionUpdate?.title,
        })),
      })
      .pipe(
        tap((response: any) => {
          this.refresh();
          this.handleOperationError(response['errors'] ?? []);
        }),
      );
  }

  getAdminPanelLink(id: string): Observable<string> {
    return this.http
      .get<AdminPanelLinkInterface>(
        `${environment.apiHost}/nodes/${id}/admin-panel-url`,
      )
      .pipe(map((response: AdminPanelLinkInterface) => response.link));
  }

  private handleOperationError(errors: ErrorInterface[]): void {
    const unknownErrorMessage = $localize`:@@nodesUnknownErrorText:Unknown error. Please try again or contact our <a href="https://${environment.vendor?.supportUrl}:VENDOR_SUPPORT_URL:" target="_blank">Support</a>`;

    errors.forEach((item: ErrorInterface) => {
      this.toastrService.error(item.message || unknownErrorMessage);
    });
  }

  getNodeModelIcon(index: string | undefined): string {
    if (!index) {
      return '';
    }

    if (
      [
        'KN-1010',
        'KN-1011',
        'KN-1012',
        'KN-1810',
        'KN-1811',
        'KN-2310',
        'KN-2311',
        'KN-2410',
        'KN-2910',
        'KN-3610',
        'KN-3710',
        'KN-3810',
      ].includes(index)
    ) {
      return 'large-4';
    } else if (
      [
        'KN-1110',
        'KN-1111',
        'KN-1112',
        'KN-1121',
        'KN-1210',
        'KN-1211',
        'KN-1212',
        'KN-1213',
        'KN-1221',
        'KN-1621',
        'KN-1721',
        'NC-1112',
        'NC-1121',
        'NC-1213',
        'NC-1221',
        'NC-1621',
        'NC-1721',
      ].includes(index)
    ) {
      return 'small-2';
    } else if (
      [
        'KN-1310',
        'KN-1311',
        'KN-1410',
        'KN-1613',
        'KN-1713',
        'KN-1714',
        'KN-2010',
        'KN-2012',
        'KN-4010',
        'NC-1613',
        'NC-1714',
        'NC-4010',
      ].includes(index)
    ) {
      return 'medium-2';
    } else if (['KN-1510', 'KN-1511'].includes(index)) {
      return 'medium-3';
    } else if (
      [
        'KN-1611',
        'KN-1710',
        'KN-1711',
        'KN-1910',
        'KN-1912',
        'KN-1913',
        'KN-2011',
        'KN-2110',
        'KN-2111',
        'KN-2112',
        'KN-2210',
        'KN-2211',
        'KN-2212',
        'KN-3010',
        'KN-3012',
        'KN-3013',
        'KN-3711',
        'KN-3811',
        'KN-3910',
        'KN-4910',
        'NC-1913',
        'NC-2212',
        'NC-3013',
        'NC-3711',
        'NC-3811',
        'NC-3910',
      ].includes(index)
    ) {
      return 'medium-4';
    } else if (['KN-2510', 'KN-2610', 'KN-2710'].includes(index)) {
      return 'extra-large-4';
    } else if (['KN-2810', 'KN-3510'].includes(index)) {
      return 'ceiling-0';
    } else if (
      [
        'KN-3210',
        'KN-3211',
        'KN-3310',
        'KN-3311',
        'KN-3410',
        'KN-3411',
        'NC-3211',
        'NC-3311',
        'NC-3411',
      ].includes(index)
    ) {
      return 'buddy-2';
    } else if (
      [
        'KN-3712',
        'KN-3812',
        'KN-3911',
        'NC-3712',
        'NC-3812',
        'NC-3911',
      ].includes(index)
    ) {
      return 'tabletop-0';
    } else if (['KN-4410'].includes(index)) {
      return 'buddy-6se-0';
    } else if (!index.includes('KN-')) {
      return 'old-2';
    } else {
      return '';
    }
  }
}
